import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Description as DescriptionIcon,
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Close as CloseIcon,
} from '@mui/icons-material';

import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../config/api';

const DocumentsTab = () => {
  const { user, tokenManager } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [documentOpen, setDocumentOpen] = useState(false);
  const [currentDocument, setCurrentDocument] = useState(null);

  // Fetch documents for the current user
  useEffect(() => {
    if (user?.id) {
      fetchDocuments();
    }
  }, [user?.id]);

  const fetchDocuments = async () => {
    if (!user?.id) return;

    setLoadingDocuments(true);
    try {
      // Fetch from Employee Document submodule
      const employeeQueryParams = new URLSearchParams();
      employeeQueryParams.append('module', 'Process Document');
      employeeQueryParams.append('subModule', 'Employee Document');
      employeeQueryParams.append('userId', user.id);

      const employeeResponse = await fetch(`${API_URL}/documents/user?${employeeQueryParams}`, {
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`,
        },
      });

      // Fetch from HR Document submodule
      const hrQueryParams = new URLSearchParams();
      hrQueryParams.append('module', 'Process Document');
      hrQueryParams.append('subModule', 'Hr Document');
      hrQueryParams.append('userId', user.id);

      const hrResponse = await fetch(`${API_URL}/documents/user?${hrQueryParams}`, {
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`,
        },
      });

      // Fetch from Existing Document submodule
      const existingQueryParams = new URLSearchParams();
      existingQueryParams.append('module', 'Process Document');
      existingQueryParams.append('subModule', 'Existing Document');
      existingQueryParams.append('userId', user.id);

      const existingResponse = await fetch(`${API_URL}/documents/user?${existingQueryParams}`, {
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`,
        },
      });

      let allDocuments = [];

      if (employeeResponse.ok) {
        const employeeResult = await employeeResponse.json();
        allDocuments = [...(employeeResult.documents || [])];
      }

      if (hrResponse.ok) {
        const hrResult = await hrResponse.json();
        allDocuments = [...allDocuments, ...(hrResult.documents || [])];
      }

      if (existingResponse.ok) {
        const existingResult = await existingResponse.json();
        allDocuments = [...allDocuments, ...(existingResult.documents || [])];
      }

      console.log('Fetched all documents:', allDocuments);
      // Debug: Log first document structure
      if (allDocuments.length > 0) {
        console.log('First document structure:', allDocuments[0]);
        console.log('All document modules:', allDocuments.map(doc => ({ 
          name: doc.documentName, 
          module: doc.module, 
          subModule: doc.subModule 
        })));
      }
      setDocuments(allDocuments);
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoadingDocuments(false);
    }
  };

  const handleViewDocument = (doc) => {
    setCurrentDocument(doc);
    setDocumentOpen(true);
  };

  const handleCloseDocument = () => {
    setDocumentOpen(false);
    setCurrentDocument(null);
  };

  const handleDownloadDocument = async (doc) => {
    try {
      const fileName = doc.filePath || doc.fileName;
      
      if (!fileName) {
        toast.error('File not found');
        return;
      }

      // Show downloading toast
      toast.info(`Downloading ${doc.documentName || fileName}...`);

      // Fetch the file with authorization header first
      const response = await fetch(`${API_URL}/documents/download/${fileName}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`,
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          toast.error('Authentication failed. Please login again.');
        } else {
          toast.error('Download failed');
        }
        return;
      }

      // Get the blob from the response
      const blob = await response.blob();
      
      // Determine file extension from original filename or content type
      let fileExtension = '';
      if (fileName.includes('.')) {
        fileExtension = fileName.split('.').pop().toLowerCase();
      } else {
        // Fallback based on content type
        const contentType = response.headers.get('content-type');
        if (contentType) {
          if (contentType.includes('image/jpeg') || contentType.includes('image/jpg')) {
            fileExtension = 'jpg';
          } else if (contentType.includes('image/png')) {
            fileExtension = 'png';
          } else if (contentType.includes('image/gif')) {
            fileExtension = 'gif';
          } else if (contentType.includes('image/webp')) {
            fileExtension = 'webp';
          } else if (contentType.includes('application/pdf')) {
            fileExtension = 'pdf';
          }
        }
      }
      
      // Get the filename from Content-Disposition header (backend handles proper formatting)
      let downloadName = doc.documentName || fileName;
      
      // Try to get the formatted filename from the response header
      const contentDisposition = response.headers.get('content-disposition');
      
      if (contentDisposition) {
        // Extract filename from Content-Disposition header
        const filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
        if (filenameMatch && filenameMatch[1]) {
          downloadName = filenameMatch[1];
        }
      }
      
      // If no proper filename from header, fallback to document name with extension
      if (!contentDisposition || !contentDisposition.includes('filename=')) {
        if (fileExtension && !downloadName.toLowerCase().endsWith(`.${fileExtension}`)) {
          downloadName += `.${fileExtension}`;
        }
      }
      
      // Create a download link with unique identifier
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = downloadName;
      link.style.display = 'none';
      link.id = `download-link-${Date.now()}`; // Unique ID
      
      // Add to DOM and trigger download
      document.body.appendChild(link);
      
      // Use requestAnimationFrame to ensure DOM is updated
      requestAnimationFrame(() => {
        link.click();
        
        // Clean up after download starts
        setTimeout(() => {
          if (document.body.contains(link)) {
            document.body.removeChild(link);
          }
          window.URL.revokeObjectURL(url);
        }, 500); // Increased delay for Chrome
      });

    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download document');
    }
  };

  const handleDelete = async (documentId) => {
    try {
      const response = await fetch(`${API_URL}/documents/${documentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`,
        },
      });

      if (response.ok) {
        // Remove from local state
        setDocuments((prev) => prev.filter((doc) => (doc.documentId || doc.id) !== documentId));
        toast.success('Document deleted successfully!');
      } else {
        const result = await response.json();
        toast.error(result.error || 'Failed to delete document');
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete document');
    }
  };

  const getStatusChip = (status) => {
    switch (status) {
      case 'verified':
        return (
          <Chip
            icon={<CheckCircleIcon />}
            label="Verified"
            color="success"
            size="small"
          />
        );
      case 'pending':
        return (
          <Chip
            icon={<WarningIcon />}
            label="Pending"
            color="warning"
            size="small"
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h6">Your Documents</Typography>
      </Box>

      {/* Loading State */}
      {loadingDocuments ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : documents.length === 0 ? (
        <Alert severity="info">
          No documents available.
        </Alert>
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <List>
                  {documents.map((doc) => (
                    <ListItem
                      key={doc.documentId || doc.id}
                      divider
                      sx={{
                        '&:last-child': {
                          borderBottom: 'none',
                        },
                      }}
                    >
                      <ListItemIcon>
                        <DescriptionIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={doc.documentName || doc.name}
                        secondary={`Uploaded on: ${new Date(doc.uploadedAt || doc.uploadDate).toLocaleDateString()} `}
                      />
                      <ListItemSecondaryAction>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <IconButton
                            edge="end"
                            aria-label="view"
                            onClick={() => handleViewDocument(doc)}
                            color="primary"
                          >
                            <VisibilityIcon />
                          </IconButton>
                          <IconButton
                            edge="end"
                            aria-label="download"
                            onClick={() => handleDownloadDocument(doc)}
                            color="primary"
                          >
                            <DownloadIcon />
                          </IconButton>
                          {/* Only show delete button for Existing Document submodule */}
                          {(doc.subModule === 'Existing Document' || doc.module === 'Existing Document') && (
                            <IconButton
                              edge="end"
                              aria-label="delete"
                              onClick={() => handleDelete(doc.documentId || doc.id)}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </Box>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Document View Dialog */}
      <Dialog 
        open={documentOpen} 
        onClose={handleCloseDocument} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
          {currentDocument?.documentName}
          <IconButton
            aria-label="close"
            onClick={handleCloseDocument}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {currentDocument && (
            <>
              {/* Display images directly */}
              {currentDocument.docType && currentDocument.docType.startsWith('image/') ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <img
                    src={`${API_URL}/documents/stream/${currentDocument.filePath || currentDocument.fileName}`}
                    alt={currentDocument.documentName}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '600px',
                      objectFit: 'contain',
                      border: '1px solid #e0e0e0',
                      borderRadius: '4px'
                    }}
                    onError={(e) => {
                      console.error('Error loading image:', e);
                      e.target.style.display = 'none';
                      // Show error message
                      const errorDiv = document.createElement('div');
                      errorDiv.innerHTML = `
                        <div style="text-align: center; padding: 20px; color: #666;">
                          <p>Failed to load image</p>
                          <p>${currentDocument.documentName}</p>
                        </div>
                      `;
                      e.target.parentNode.appendChild(errorDiv);
                    }}
                  />
                </Box>
              ) : (
                /* Display PDFs and other documents in iframe */
                <iframe
                  src={`${API_URL}/documents/stream/${currentDocument.filePath || currentDocument.fileName}`}
                  width="100%"
                  height="600px"
                  title={currentDocument.documentName}
                  style={{ border: 'none' }}
                  onError={(e) => {
                    console.error('Error loading document:', e);
                  }}
                />
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDocument}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DocumentsTab; 